import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { formatMoney } from '../utils/adminFormat';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  FormControl,
  Select,
  MenuItem,
} from '@mui/material';
import QuizIcon from '@mui/icons-material/Quiz';
import DownloadIcon from '@mui/icons-material/Download';
import api from '../api';
// Shared so every billing tab shows the same London-time format.
import { formatUkDateTime } from '../utils/mobileSubscriptionFormat';
import { toast } from 'react-toastify';
import BillingFilterBar, { matchesSearch, normalisePaymentStatus } from './BillingFilterBar';
import BillingPagination, { useBillingPagination } from './BillingPagination';

const MockExamSubscriptionList = () => {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const {
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage,
    paginate,
  } = useBillingPagination(`${search}|${statusFilter}`);
  // Same search + status filter as the Papers tab (BillingFilterBar), applied client-side.
  // Paging is also client-side: the list is one parent's purchases.
  const visibleRows = useMemo(
    () =>
      rows.filter(
        (row) =>
          (!statusFilter || normalisePaymentStatus(row.payment_status) === statusFilter) &&
          matchesSearch(search, [
            row.exam_name,
            row.school_name,
            row.grade_name,
            row.subject_names,
            row.category,
            row.student_name,
          ])
      ),
    [rows, search, statusFilter]
  );
  const { page, count: rowCount, rows: pagedRows } = paginate(visibleRows);
  const [loading, setLoading] = useState(true);
  const [parentStudents, setParentStudents] = useState([]);
  const [assigningPurchaseId, setAssigningPurchaseId] = useState(null);
  const [downloadingPurchaseId, setDownloadingPurchaseId] = useState(null);
  const autoAssignedRef = useRef(new Set());

  const isBundlePurchase = (row) => row?.type === 'bundle';
  const purchaseRowKey = (row) => `${row?.type || 'mock_exam'}-${row?.purchase_id}`;

  const loadPurchases = async () => {
    setLoading(true);
    try {
      const response = await api.get('parent/mock-exam/purchases');
      if (response.data?.success) {
        const raw = response.data.data;
        const list = Array.isArray(raw) ? raw : [];
        setRows(list);
      } else {
        setRows([]);
        toast.error(response.data?.message || 'Failed to load mock exam purchases');
      }
    } catch (error) {
      setRows([]);
      if (error.response?.status === 401) {
        toast.error('Please log in as a parent to view mock exam purchases.');
      } else {
        toast.error(error.response?.data?.message || 'Failed to load mock exam purchases');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPurchases();
  }, []);

  useEffect(() => {
    const fetchStudents = async () => {
      try {
        const response = await api.get('parent/students/names');
        if (response.data.success && Array.isArray(response.data.data)) {
          setParentStudents(response.data.data);
        }
      } catch (err) {
        console.error('Error fetching parent students for mock exam assign:', err);
      }
    };
    fetchStudents();
  }, []);

  const handleAssignStudent = useCallback(async (purchaseId, studentId) => {
    setAssigningPurchaseId(purchaseId);
    try {
      const response = await api.put(`parent/mock-exam/purchases/${purchaseId}/assign-student`, {
        student_id: studentId ? parseInt(studentId, 10) : null,
      });
      if (response.data.success) {
        toast.success(response.data.message || 'Assignment updated.');
        const data = response.data.data || {};
        setRows((prev) =>
          prev.map((p) =>
            p.purchase_id === purchaseId
              ? {
                  ...p,
                  student_id: data.student_id ?? null,
                  student_name: data.student_name ?? p.student_name,
                  student_email: data.student_email ?? p.student_email,
                }
              : p
          )
        );
      } else {
        toast.error(response.data.message || 'Failed to update assignment.');
      }
    } catch (error) {
      console.error('Error assigning student to mock exam:', error);
      toast.error(error.response?.data?.message || 'Failed to update assignment.');
    } finally {
      setAssigningPurchaseId(null);
    }
  }, []);

  // When parent has only one student, auto-assign purchases that have no student yet
  useEffect(() => {
    if (parentStudents.length !== 1 || rows.length === 0) return;
    const studentId = parentStudents[0].id;
    const unassigned = rows.filter(
      // A child's own purchase (can_assign false) stays with that child: never auto-assign it.
      (p) =>
        !isBundlePurchase(p) &&
        p.can_assign !== false &&
        !p.student_id &&
        !autoAssignedRef.current.has(purchaseRowKey(p))
    );
    unassigned.forEach((purchase) => {
      autoAssignedRef.current.add(purchaseRowKey(purchase));
      handleAssignStudent(purchase.purchase_id, studentId);
    });
  }, [parentStudents, rows, handleAssignStudent]);

  const handleDownloadReceipt = async (purchaseId) => {
    setDownloadingPurchaseId(purchaseId);
    try {
      const response = await api.get('parent/mock-exam/purchase/receipt', {
        params: { purchase_id: purchaseId },
      });
      if (response.data.success && response.data.data.receipt_url) {
        window.open(response.data.data.receipt_url, '_blank');
      } else {
        toast.error(response.data.message || 'Receipt URL not available.');
      }
    } catch (error) {
      console.error('Error fetching mock exam receipt:', error);
      toast.error(error.response?.data?.message || 'Failed to load receipt.');
    } finally {
      setDownloadingPurchaseId(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 10 }}>
        <CircularProgress size={40} sx={{ color: '#667eea' }} />
      </Box>
    );
  }

  if (!rows.length) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 8,
          textAlign: 'center',
          borderRadius: 4,
          border: '2px dashed rgba(102, 126, 234, 0.1)',
          bgcolor: 'rgba(255, 255, 255, 0.5)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Box
          sx={{
            width: 80,
            height: 80,
            background: 'linear-gradient(135deg, #f0f4ff 0%, #eef2ff 100%)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 1,
            boxShadow: '0 8px 24px rgba(102, 126, 234, 0.1)',
          }}
        >
          <QuizIcon sx={{ fontSize: 40, color: '#667eea' }} />
        </Box>
        <Typography variant="h6" fontWeight={700} color="#2d3748">
          No mock exam purchases yet
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 480, mx: 'auto', lineHeight: 1.6 }}>
          When you buy a mock exam from the portal, it will appear here with amount, payment status, and assigned student.
        </Typography>
      </Paper>
    );
  }

  return (
    <>
    <BillingFilterBar
      search={search}
      onSearchChange={setSearch}
      status={statusFilter}
      onStatusChange={setStatusFilter}
      placeholder="Search by mock exam, school, subject, student..."
    />
    {visibleRows.length === 0 ? (
      <Paper elevation={0} sx={{ p: 4, textAlign: 'center', borderRadius: 3, border: '1px dashed rgba(102, 126, 234, 0.2)' }}>
        <Typography variant="body1" fontWeight={600} color="#2d3748">
          No mock exam purchases match your search or status filter.
        </Typography>
      </Paper>
    ) : (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        border: '1px solid rgba(0,0,0,0.06)',
        overflow: 'hidden',
      }}
    >
    <TableContainer sx={{ overflowX: 'auto' }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ background: 'linear-gradient(135deg, #f8f9ff 0%, #e8f4fd 100%)' }}>
            <TableCell sx={{ fontWeight: 700 }}>Mock exam</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>School</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Grade / year</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Subject(s)</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Assign to student</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Amount</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Payment</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Purchased</TableCell>
            <TableCell sx={{ fontWeight: 700 }} align="center">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {pagedRows.map((row) => (
            <TableRow key={purchaseRowKey(row)} hover>
              <TableCell>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {row.exam_name || 'N/A'}
                </Typography>
                {!isBundlePurchase(row) && (
                  <Typography variant="caption" color="text.secondary">
                    {row.duration_minutes || 0} min | {row.total_marks || 0} marks
                  </Typography>
                )}
              </TableCell>
              <TableCell sx={{ maxWidth: 160 }}>
                <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                  {row.school_name || '—'}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2">{row.grade_name || '—'}</Typography>
              </TableCell>
              <TableCell sx={{ maxWidth: 220 }}>
                <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                  {row.subject_names || '—'}
                </Typography>
              </TableCell>
              <TableCell>
                <Box sx={{ position: 'relative', display: 'inline-block', minWidth: 168 }}>
                  {/* Read-only for packs, and for a purchase the child made themselves (only the
                      buyer can reassign it — the API refuses anyone else). */}
                  {isBundlePurchase(row) || row.can_assign === false || parentStudents.length === 0 ? (
                    <Typography variant="body2" color={isBundlePurchase(row) || row.can_assign === false ? 'text.primary' : 'text.secondary'} fontWeight={isBundlePurchase(row) || row.can_assign === false ? 500 : undefined}>
                      {row.student_name || 'Not assigned'}
                    </Typography>
                  ) : parentStudents.length === 1 ? (
                    <>
                      <Typography variant="body2" fontWeight={500}>
                        {parentStudents[0].full_name}
                        {assigningPurchaseId === row.purchase_id && (
                          <CircularProgress size={14} sx={{ ml: 0.5, verticalAlign: 'middle' }} />
                        )}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {row.student_email || parentStudents[0].email || '—'}
                      </Typography>
                    </>
                  ) : (
                    <>
                      <FormControl size="small" sx={{ minWidth: 160 }} disabled={assigningPurchaseId === row.purchase_id}>
                        <Select
                          value={row.student_id ?? ''}
                          onChange={(e) => handleAssignStudent(row.purchase_id, e.target.value)}
                          displayEmpty
                          sx={{
                            fontSize: '0.875rem',
                            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
                          }}
                        >
                          <MenuItem value="">
                            <em>Unassigned</em>
                          </MenuItem>
                          {parentStudents.map((s) => (
                            <MenuItem key={s.id} value={s.id}>
                              {s.full_name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      {assigningPurchaseId === row.purchase_id && (
                        <CircularProgress
                          size={18}
                          sx={{ position: 'absolute', right: 28, top: '50%', transform: 'translateY(-50%)' }}
                        />
                      )}
                    </>
                  )}
                </Box>
              </TableCell>
              <TableCell>
                {formatMoney(row.price || 0, row.currency || 'GBP')}
              </TableCell>
              <TableCell>
                <Chip
                  size="small"
                  label={row.payment_status || 'PENDING'}
                  color={(row.payment_status || '').toUpperCase() === 'PAID' ? 'success' : 'warning'}
                  variant="outlined"
                />
              </TableCell>
              <TableCell>{formatUkDateTime(row.purchased_at)}</TableCell>
              <TableCell align="center">
                {!isBundlePurchase(row) && (row.payment_status || '').toUpperCase() === 'PAID' && (
                  <Box
                    component="button"
                    onClick={() => handleDownloadReceipt(row.purchase_id)}
                    disabled={downloadingPurchaseId === row.purchase_id}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.5,
                      border: '1px solid #16a34a',
                      bgcolor: 'rgba(22, 163, 74, 0.05)',
                      color: '#16a34a',
                      px: 1.5,
                      py: 0.5,
                      borderRadius: '6px',
                      fontSize: '12px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': {
                        bgcolor: 'rgba(22, 163, 74, 0.1)',
                      },
                      '&:disabled': {
                        opacity: 0.5,
                        cursor: 'not-allowed',
                      },
                    }}
                  >
                    {downloadingPurchaseId === row.purchase_id ? (
                      <CircularProgress size={14} color="inherit" />
                    ) : (
                      <DownloadIcon sx={{ fontSize: 16 }} />
                    )}
                    Receipt
                  </Box>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
    <BillingPagination
      count={rowCount}
      page={page}
      rowsPerPage={rowsPerPage}
      onPageChange={handleChangePage}
      onRowsPerPageChange={handleChangeRowsPerPage}
    />
    </Paper>
    )}
    </>
  );
};

export default MockExamSubscriptionList;
